/* eslint-disable react/require-default-props */
import React, { useCallback, useState, useMemo } from 'react';
import { Modal } from '@components/v2/Modal';
import { Button, ConfirmDialog } from '@ui';
import { Toggle } from '@ui/molecules/inputs/Toggle';
import { InlineEdit } from '@components/v2/InlineEdit';
import { translate } from '@utils/i18n';
import { TRANSLATION_LANGUAGES } from '@constants/translationLanguages';
import LegacyConfirmDialog from '@components/ConfirmDialog/ConfirmDialog';
import Banner, { BannerVariant } from '@components/Banner';
import { ATTACHABLE_TYPE_FOR_CHILD_OF_COMPONENT as PARENT_COMPONENT } from '@features/Components/utils/componentCompatibility';
import ComponentTypeSelector from './ComponentTypeSelector';
import AttachableTypeSelector from './AttachableTypeSelector';
import InlineFieldDefinitionsBuilder, { validateInlineFieldDefinitions } from './InlineFieldDefinitionsBuilder';
import CategorySelector from './CategorySelector';
import EpCodeSelector from './EpCodeSelector';
import DnvTypeSelector from './DnvTypeSelector';
import './ComponentTypeDetailModalStyles.scss';
const namespace = 'features.settings.component_types';
const ATTACHABLE_TYPES_WARNING_KEYS = {
    mixedTypes: {
        title: 'parent_component_mixed_types_title',
        description: 'parent_component_mixed_types_description'
    },
    parentComponentOnParentType: {
        title: 'parent_component_on_parent_type_title',
        description: 'parent_component_on_parent_type_description'
    }
};
function attachableTypesWarningFor(currentTypes, newTypes, compatibleChildTypeIds) {
    const addedTypes = newTypes.filter((type) => !currentTypes.includes(type));
    if (currentTypes.includes(PARENT_COMPONENT) && addedTypes.some((type) => type !== PARENT_COMPONENT)) {
        return 'mixedTypes';
    }
    const hasDeviceTypesOrSubcomponents = currentTypes.length > 0 || compatibleChildTypeIds.length > 0;
    if (addedTypes.includes(PARENT_COMPONENT) && hasDeviceTypesOrSubcomponents) {
        return 'parentComponentOnParentType';
    }
    return null;
}
// Debounce utility function
const useDebounce = (callback, delay) => {
    const timeoutRef = React.useRef();
    return useCallback((typeId, field, value) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => callback(typeId, field, value), delay);
    }, [callback, delay]);
};
export default function ComponentTypeDetailModal({ isOpen, onClose, componentType, allComponentTypes, allowedAttachableTypes, onUpdate, onToggleActive, isDeleting = false, supportsSubcomponents, hasJointCommission = false, hasServiceTradeIntegration = false, hasDnvReporting = false, dnvTypeOptions = [], translationLanguages = [] }) {
    var _a;
    const [isActionConfirmOpen, setIsActionConfirmOpen] = useState(false);
    // Capture original field keys - computed on each render to ensure correct value on first render
    const originalFieldKeys = useMemo(() => {
        var _a;
        if (isOpen && componentType) {
            return new Set((((_a = componentType.fieldDefinitions) === null || _a === void 0 ? void 0 : _a.fields) || []).map((field) => field.key));
        }
        return new Set();
    }, [isOpen, componentType]);
    // Debounced update function (500ms delay)
    const debouncedUpdate = useDebounce((typeId, field, value) => {
        void onUpdate(typeId, field, value);
    }, 500);
    const handleFieldUpdate = (field, value) => {
        if (componentType) {
            // Special case: batch EP code updates to avoid race condition
            if (field === 'epCodeUpdate') {
                const { epCode, ecType } = value;
                // Check if either value changed
                const epCodeChanged = JSON.stringify(componentType.epCode) !== JSON.stringify(epCode);
                const ecTypeChanged = JSON.stringify(componentType.ecType) !== JSON.stringify(ecType);
                if (epCodeChanged || ecTypeChanged) {
                    // Send both fields in a single update, preserving null for clearing
                    debouncedUpdate(componentType.id, field, {
                        epCode,
                        ecType: ecType
                    });
                }
                return;
            }
            const currentValue = componentType[field];
            if (JSON.stringify(currentValue) === JSON.stringify(value))
                return;
            debouncedUpdate(componentType.id, field, value);
        }
    };
    const handleAction = () => {
        if (componentType) {
            setIsActionConfirmOpen(true);
        }
    };
    const handleConfirmAction = () => {
        if (componentType) {
            void onToggleActive(componentType.id);
        }
        setIsActionConfirmOpen(false);
    };
    const handleCancelAction = () => {
        setIsActionConfirmOpen(false);
    };
    const handleFieldDefinitionsChange = (newFields) => {
        // Validate before saving
        const validationErrors = validateInlineFieldDefinitions(newFields);
        // Only save if there are no validation errors
        if (validationErrors.length === 0) {
            handleFieldUpdate('fieldDefinitions', { fields: newFields });
        }
    };
    const handleCompatibilitiesChange = (typeIds) => {
        handleFieldUpdate('compatibleChildTypeIds', typeIds);
    };
    const [pendingAttachableTypes, setPendingAttachableTypes] = useState(null);
    const handleAllowedAttachableTypesChange = (types) => {
        const warning = attachableTypesWarningFor((componentType === null || componentType === void 0 ? void 0 : componentType.allowedAttachableTypes) || [], types, (componentType === null || componentType === void 0 ? void 0 : componentType.compatibleChildTypeIds) || []);
        if (warning) {
            setPendingAttachableTypes({ types, warning });
            return;
        }
        handleFieldUpdate('allowedAttachableTypes', types);
    };
    const applyPendingAttachableTypes = () => {
        if (pendingAttachableTypes) {
            handleFieldUpdate('allowedAttachableTypes', pendingAttachableTypes.types);
        }
        setPendingAttachableTypes(null);
    };
    const cancelPendingAttachableTypes = () => {
        setPendingAttachableTypes(null);
    };
    if (!componentType)
        return null;
    const modalTitle = (React.createElement("div", { className: "component-type-detail-modal__header" },
        React.createElement("div", { className: "component-type-detail-modal__header-content" },
            React.createElement("span", null,
                React.createElement("b", null,
                    translate('type_column', { namespace }),
                    ":"),
                " ",
                componentType.typeName)),
        React.createElement("div", null,
            React.createElement(Button, { onClick: handleAction, disabled: isDeleting }, isDeleting ? (React.createElement("i", { className: "fa fa-spinner fa-spin" })) : (React.createElement(React.Fragment, null,
                React.createElement("i", { className: componentType.active ? 'fa-light fa-ban' : 'fa-light fa-check-circle' }),
                ' ',
                componentType.active
                    ? translate('deactivate', { namespace: 'features.commons' })
                    : translate('activate', { namespace: 'features.commons' })))))));
    const hasExistingComponents = componentType.componentsCount > 0;
    const allowedTypes = componentType.allowedAttachableTypes || [];
    const isNestedOnly = allowedTypes.length > 0 && allowedTypes.every((type) => type === PARENT_COMPONENT);
    const isOfferedAsSubcomponent = allComponentTypes.some((type) => { var _a; return (_a = type.compatibleChildTypeIds) === null || _a === void 0 ? void 0 : _a.includes(componentType.id); });
    const pendingWarningKeys = pendingAttachableTypes && ATTACHABLE_TYPES_WARNING_KEYS[pendingAttachableTypes.warning];
    return (React.createElement(Modal, { closeOnOverlayClick: true, title: modalTitle, isOpen: isOpen, onClose: onClose, width: "800px", className: "component-type-detail-modal" },
        React.createElement("div", { className: "component-type-detail-form" },
            hasExistingComponents && (React.createElement(Banner, { text: translate('restrictions_banner', { namespace }), variant: BannerVariant.Info })),
            React.createElement("div", { className: "form-section" },
                React.createElement("div", { className: "form-field" },
                    React.createElement("label", { className: "form-field__label" },
                        translate('category', { namespace }),
                        ":"),
                    React.createElement(CategorySelector, { label: "", variant: "inline", value: componentType.category || '', onChange: (value) => handleFieldUpdate('category', value || null), onBlur: () => {
                            // No-op: blur handled by debounced auto-save
                        } })),
                React.createElement("div", { className: "form-field" },
                    React.createElement("label", { className: "form-field__label" },
                        translate('description_column', { namespace }),
                        ":"),
                    React.createElement(InlineEdit, { as: "div", className: "--x-full", onBlur: (e) => {
                            const target = e.target;
                            handleFieldUpdate('description', target.textContent || '');
                        } }, componentType.description || '')),
                React.createElement("div", { className: "form-field" },
                    React.createElement("label", { id: "component-type-name-en-label", className: "form-field__label" },
                        translate('type_name', { namespace }),
                        ":"),
                    React.createElement(InlineEdit, { as: "div", className: "--x-full", "aria-labelledby": "component-type-name-en-label", onBlur: (e) => {
                            const target = e.target;
                            handleFieldUpdate('typeNameEn', target.textContent || '');
                        } }, componentType.typeNameEn || '')),
                translationLanguages.includes(TRANSLATION_LANGUAGES.FR) && (React.createElement("div", { className: "form-field" },
                    React.createElement("label", { id: "component-type-name-fr-label", className: "form-field__label" },
                        translate('type_name_fr', { namespace }),
                        ":"),
                    React.createElement(InlineEdit, { as: "div", className: "--x-full", "aria-labelledby": "component-type-name-fr-label", placeholder: translate('type_name_fr_placeholder', { namespace }), onBlur: (e) => {
                            const target = e.target;
                            handleFieldUpdate('typeNameFr', target.textContent || '');
                        } }, componentType.typeNameFr || ''))),
                translationLanguages.includes(TRANSLATION_LANGUAGES.ES) && (React.createElement("div", { className: "form-field" },
                    React.createElement("label", { id: "component-type-name-es-label", className: "form-field__label" },
                        translate('type_name_es', { namespace }),
                        ":"),
                    React.createElement(InlineEdit, { as: "div", className: "--x-full", "aria-labelledby": "component-type-name-es-label", placeholder: translate('type_name_es_placeholder', { namespace }), onBlur: (e) => {
                            const target = e.target;
                            handleFieldUpdate('typeNameEs', target.textContent || '');
                        } }, componentType.typeNameEs || '')))),
            hasJointCommission && (React.createElement("div", { className: "form-section" },
                React.createElement("h3", { className: "form-section__title" }, translate('joint_commission_settings', { namespace })),
                React.createElement("div", { className: "form-field" },
                    React.createElement("label", { className: "form-field__label" },
                        translate('joint_commission_title', { namespace }),
                        ":"),
                    React.createElement(InlineEdit, { as: "div", className: "--x-full", placeholder: "", onBlur: (e) => {
                            const target = e.target;
                            handleFieldUpdate('jointCommissionTitle', target.textContent || '');
                        } }, componentType.jointCommissionTitle || '')),
                React.createElement(EpCodeSelector, { epCode: componentType.epCode, ecType: componentType.ecType, onChange: (epCode, ecType) => {
                        handleFieldUpdate('epCodeUpdate', { epCode, ecType });
                    }, variant: "edit" }))),
            hasDnvReporting && (React.createElement("div", { className: "form-section" },
                React.createElement("h3", { className: "form-section__title" }, translate('dnv_settings', { namespace })),
                React.createElement(DnvTypeSelector, { dnvType: componentType.dnvType, options: dnvTypeOptions, onChange: (dnvType) => handleFieldUpdate('dnvType', dnvType), variant: "edit" }))),
            hasServiceTradeIntegration && (React.createElement("div", { className: "form-section" },
                React.createElement("h3", { className: "form-section__title" }, translate('service_trade_settings', { namespace })),
                React.createElement("div", { className: "form-field" },
                    React.createElement("label", { className: "form-field__label" },
                        translate('service_line_abbrs', { namespace }),
                        ":"),
                    React.createElement(InlineEdit, { as: "div", className: "--x-full", placeholder: translate('service_line_abbrs_placeholder', { namespace }), onBlur: (e) => {
                            var _a;
                            const target = e.target;
                            const value = ((_a = target.textContent) === null || _a === void 0 ? void 0 : _a.trim()) || '';
                            handleFieldUpdate('serviceLineAbbrs', value === '' ? null : value);
                        } }, componentType.serviceLineAbbrs || ''),
                    React.createElement("span", { className: "form-field__help-text" }, translate('service_line_abbrs_help', { namespace }))))),
            supportsSubcomponents && (React.createElement("div", { className: "form-section" },
                React.createElement("h3", { className: "form-section__title" }, translate('compatible_subcomponents', { namespace })),
                React.createElement(ComponentTypeSelector, { selectedTypeIds: componentType.compatibleChildTypeIds || [], availableTypes: allComponentTypes, currentTypeId: componentType.id, onChange: handleCompatibilitiesChange, lockedTypeIds: componentType.childTypeIdsInUse || [] }))),
            React.createElement("div", { className: "form-section" },
                React.createElement("h3", { className: "form-section__title" }, translate('allowed_parent_device_types', { namespace })),
                React.createElement(Banner, { variant: BannerVariant.Info, text: translate('allowed_device_types_help', { namespace }) }),
                allowedTypes.length === 0 && (React.createElement(Banner, { variant: BannerVariant.Warning, text: translate('not_yet_configured_banner', { namespace }) })),
                isNestedOnly && !isOfferedAsSubcomponent && (React.createElement(Banner, { variant: BannerVariant.Warning, text: translate('nested_only_unreachable_banner', { namespace }) })),
                React.createElement(AttachableTypeSelector, { selectedTypes: allowedTypes, availableTypes: allowedAttachableTypes, onChange: handleAllowedAttachableTypesChange, lockedTypes: componentType.attachableTypesInUse || [] })),
            React.createElement("div", { className: "form-section" },
                React.createElement("h3", { className: "form-section__title" }, translate('report_generation_settings', { namespace })),
                React.createElement("div", { className: "report-generation-toggle" },
                    React.createElement(Toggle, { checked: componentType.supportsIndividualReports, onChange: (e) => handleFieldUpdate('supportsIndividualReports', e.target.checked), label: translate('supports_individual_reports', { namespace }), helperText: translate('supports_individual_reports_help', { namespace }) }))),
            React.createElement("div", { className: "form-section" },
                React.createElement("h3", { className: "form-section__title" }, translate('component_specific_fields', { namespace })),
                React.createElement(InlineFieldDefinitionsBuilder, { translationLanguages: translationLanguages, fieldDefinitions: ((_a = componentType.fieldDefinitions) === null || _a === void 0 ? void 0 : _a.fields) || [], onChange: handleFieldDefinitionsChange, hasExistingComponents: hasExistingComponents, originalFieldKeys: originalFieldKeys }))),
        isActionConfirmOpen && (React.createElement(LegacyConfirmDialog, { title: translate(componentType.active ? 'deactivate_type_title' : 'activate_type_title', { namespace }), description: translate(componentType.active ? 'deactivate_type_description' : 'activate_type_description', {
                namespace
            }), onConfirm: handleConfirmAction, onCancel: handleCancelAction, yesLabel: translate(componentType.active ? 'deactivate' : 'activate', { namespace: 'features.commons' }), noLabel: translate('cancel', { namespace: 'features.commons' }) })),
        pendingWarningKeys && (React.createElement(ConfirmDialog, { isOpen: true, title: translate(pendingWarningKeys.title, { namespace }), description: translate(pendingWarningKeys.description, { namespace }), onConfirm: applyPendingAttachableTypes, onCancel: cancelPendingAttachableTypes }))));
}
